import React from 'react';

/**
 * FilterChip Props
 */
interface FilterChipProps {
  /** Display label for the filter */
  label: string;
  /** Optional value (e.g., "Active", "Blue Mint") */
  value?: string;
  /** Callback when chip is removed */
  onRemove?: () => void;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * FilterChip Component
 * Displays an active filter as a removable chip/pill
 *
 * Spec: Uses .c-filter-chip CSS class per Carrot_Filtering_System_Specification.md
 *
 * Accessibility:
 * - role="button" with tabIndex for keyboard access
 * - aria-label describes the filter and removal action
 * - Supports Enter and Space key for removal
 * - Visually hidden text for screen readers
 */
export function FilterChip({ label, value, onRemove, disabled = false }: FilterChipProps) {
  const handleRemove = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled || !onRemove) return;

    e.preventDefault();
    e.stopPropagation();
    onRemove();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || !onRemove) return;

    // Support Enter and Space for removal
    if (e.key === 'Enter' || e.key === ' ') {
      handleRemove(e);
    }
  };

  const displayText = value ? `${label}: ${value}` : label;
  const ariaLabel = `Remove filter ${displayText}`;

  const className = `c-filter-chip${disabled ? ' is-disabled' : ''}`;

  return (
    <span
      className={className}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      <span>{displayText}</span>
      {onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          aria-label={ariaLabel}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            padding: '0 0 0 0.35em',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '1.2em',
            lineHeight: '1',
          }}
        >
          <span aria-hidden="true">×</span>
          <span className="u-visually-hidden">Remove</span>
        </button>
      )}
    </span>
  );
}
